import React, { lazy } from "react";
import { MODULES } from "../../constants/modules";
import { PERMISSIONS, ROLES } from "../../constants/permissions";
import {
  DashboardOutlined,
  UserOutlined,
  FileTextOutlined,
  SettingOutlined,
  SafetyCertificateOutlined,
  KeyOutlined,
  BookOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  CalculatorOutlined,
  FileProtectOutlined,
  TeamOutlined,
  CalendarOutlined,
  BankOutlined,
  ProjectOutlined,
  FieldTimeOutlined,
  CrownOutlined,
  GlobalOutlined,
  ShopOutlined,
  BuildOutlined,
  BellOutlined,
  CheckOutlined,
  AimOutlined,
  BarChartOutlined,
  AppstoreOutlined,
  BranchesOutlined,
} from "@ant-design/icons";

// Lazy-loaded components
const Dashboard = lazy(() => import("../Pages/Dashboard"));
const EmployeeDashboard = lazy(() =>
  import("../Pages/Employee/EmployeeDashboard")
);
const DynamicDashboard = lazy(() => import("../Pages/DynamicDashboard"));
const CompanyMaster = lazy(() => import("../Pages/Sales/CompanyMaster"));
const EmployeeMaster = lazy(() => import("../Pages/Employee/EmployeeMaster"));
const EmployeeFormPage = lazy(() =>
  import("../Pages/Employee/EmployeeFormPage")
);
const ItemMaster = lazy(() => import("../Pages/Design/ItemMaster"));
const Quotations = lazy(() => import("../Pages/Sales/Quotations"));
const ClientMaster = lazy(() => import("../Pages/CRM/ClientMaster"));
const LeadSourceMaster = lazy(() => import("../Pages/CRM/LeadSourceMaster"));
const LeadMaster = lazy(() => import("../Pages/CRM/LeadMaster"));
const OpportunityMaster = lazy(() => import("../Pages/CRM/OpportunityMaster"));
const ProductMaster = lazy(() => import("../Pages/CRM/ProductMaster"));
const LeaveMaster = lazy(() => import("../Pages/HR/LeaveMaster"));
const HolidayMaster = lazy(() => import("../Pages/HR/HolidayMaster"));
const EmployeeLeaveAllocationMaster = lazy(() =>
  import("../Pages/HR/EmployeeLeaveAllocationMaster")
);
const EmployeeDirectory = lazy(() => import("../Pages/Employee/EmployeeDirectory"));
const EmployeeReimbursementMaster = lazy(() =>
  import("../Pages/HR/EmployeeReimbursementMaster")
);
const AttendanceRecordMaster = lazy(() =>
  import("../Pages/HR/AttendanceRecordMaster")
);
const ReimbursementTypeMaster = lazy(() =>
  import("../Pages/HR/ReimbursementTypeMaster")
);
const ShiftMaster = lazy(() => import("../Pages/HR/ShiftMaster"));
const EmployeeApplyLeave = lazy(() => import("../Pages/HR/EmployeeApplyLeave"));

const PayrollComponentMaster = lazy(() =>
  import("../Pages/HR/PayrollComponentMaster")
);
const EmployeeSalaryStructureMaster = lazy(() =>
  import("../Pages/HR/EmployeeSalaryStructureMaster")
);
const PayslipMaster = lazy(() => import("../Pages/HR/PayslipMaster"));
const PayslipComponentMaster = lazy(() =>
  import("../Pages/HR/PayslipComponentMaster")
);
const PayslipGenerator = lazy(() => import("../Pages/HR/PayslipGenerator"));
const BulkPayslipGenerator = lazy(() =>
  import("../Pages/HR/BulkPayslipGenerator")
);
const PayslipStatusManager = lazy(() =>
  import("../Pages/HR/PayslipStatusManager")
);
const PayslipView = lazy(() => import("../Pages/HR/PayslipView"));
const PayrollAnalytics = lazy(() => import("../Pages/HR/PayrollAnalytics"));
const PayrollTools = lazy(() => import("../Pages/HR/PayrollTools"));
const SalaryTemplateMaster = lazy(() =>
  import("../Pages/HR/SalaryTemplateMaster")
);
const EmployeeTemplateAssignment = lazy(() =>
  import("../Pages/HR/EmployeeTemplateAssignment")
);
const CompanyContactMaster = lazy(() =>
  import("../Pages/Sales/CompanyContactMaster")
);
const DepartmentManagement = lazy(() =>
  import("../Pages/HR/DepartmentManagement")
);
const DesignationManagement = lazy(() =>
  import("../Pages/HR/DesignationManagement")
);
const WorkerTypeManagement = lazy(() =>
  import("../Pages/HR/WorkerTypeManagement")
);
// const AuthDebug = lazy(() => import("../Pages/HR/AuthDebug")); // Commented out - debug route

const ProjectMaster = lazy(() => import("../Pages/Project/ProjectMaster"));
const ProjectAssignmentMaster = lazy(() =>
  import("../Pages/Project/ProjectAssignmentMaster")
);
// const TaskMaster = lazy(() => import("../Pages/Project/TaskMaster")); // Commented out - will be used for Jira clone later
const TimesheetMaster = lazy(() => import("../Pages/HR/TimesheetMaster"));
const WeeklyTimesheetMaster = lazy(() =>
  import("../Pages/HR/WeeklyTimesheetMaster")
);
const TimeEntryMaster = lazy(() => import("../Pages/HR/TimeEntryMaster"));
const WeeklyTimesheetForm = lazy(() =>
  import("../Pages/HR/WeeklyTimesheetForm")
);

// Role & Permission Management Components
const RolePermissionDashboard = lazy(() =>
  import("../Pages/HR/RolePermissionDashboard")
);
const RolePermissionGuide = lazy(() =>
  import("../Pages/HR/RolePermissionGuide")
);
const NotificationCenter = lazy(() =>
  import("../Pages/HR/NotificationCenter")
);
const PerformanceReview = lazy(() => import("../Pages/HR/PerformanceReview"));
const PerformanceGoal = lazy(() => import("../Pages/HR/PerformanceGoal"));
const JobPosting = lazy(() => import("../Pages/HR/JobPosting"));
const JobApplication = lazy(() => import("../Pages/HR/JobApplication"));
const TrainingProgram = lazy(() => import("../Pages/HR/TrainingProgram"));
const EmployeeTraining = lazy(() => import("../Pages/HR/EmployeeTraining"));
const LeaveEncashment = lazy(() => import("../Pages/HR/LeaveEncashment"));
const LeaveCarryForward = lazy(() => import("../Pages/HR/LeaveCarryForward"));
const InterviewFeedback = lazy(() =>
  import("../Pages/HR/InterviewFeedback")
);

// Admin Components
const AdminPoliciesPage = lazy(() => import("../Pages/Admin/AdminPoliciesPage"));
const AdminEmployeeDocumentsPage = lazy(() => import("../Pages/HR/AdminEmployeeDocumentsPage"));
const SystemConfiguration = lazy(() =>
  import("../Pages/Admin/SystemConfiguration")
);
const TenantConfig = lazy(() => import("../Pages/Admin/TenantConfig"));
const TenantManagement = lazy(() => import("../Pages/Admin/TenantManagement"));
const SubscriptionManagement = lazy(() => import("../Pages/Admin/SubscriptionManagement"));
const EmployeeProfilePage = lazy(() => import("../Pages/Employee/EmployeeProfilePage"));

const routes = [
  // Hidden redirect routes (must be first)
  {
    key: "admin-profile",
    path: "/admin/profile",
    title: "Admin Profile",
    icon: <UserOutlined />,
    component: EmployeeProfilePage,
    hidden: true,
    protected: true,
  },
  {
    key: "my-self-service",
    path: "/employee",
    title: "My Self Service",
    icon: <UserOutlined />,
    hidden: false,
    protected: true,
  },
  {
    key: "employee-redirect",
    path: "/employee",
    title: "Employee",
    icon: <UserOutlined />,
    component: EmployeeMaster,
    hidden: true,
    protected: true,
  },
  {
    key: "employee-create-redirect",
    path: "/employee/create",
    title: "Create Employee",
    icon: <UserOutlined />,
    component: EmployeeFormPage,
    hidden: true,
    protected: true,

  },
  {
    key: "employee-edit-redirect",
    path: "/employee/edit/:id",
    title: "Edit Employee",
    icon: <UserOutlined />,
    component: EmployeeFormPage,
    hidden: true,
    protected: true,

  },
  // 1. Dashboard (First - Most Important)
  {
    key: "dashboard",
    path: "/dashboard",
    title: "Dashboard",
    icon: <DashboardOutlined />,
    component: DynamicDashboard,
    protected: true,
  },
  // 2. HR Management (Core HR Functions)
  {
    key: "hr",
    path: "/hr",
    title: "HR Management",
    icon: <TeamOutlined />,
    protected: true,
    module: MODULES.EMPLOYEE,
    children: [
      // 2.1 Employee Management
      {
        key: "employee-master",
        path: "/hr/employee",
        title: "Employee Master",
        icon: <UserOutlined />,
        component: EmployeeMaster,
        protected: true,

        module: MODULES.EMPLOYEE,
      },
      {
        key: "employee-directory",
        path: "/hr/employee-directory",
        title: "Employee Directory",
        icon: <TeamOutlined />,
        component: EmployeeDirectory,
        protected: true,

        module: MODULES.EMPLOYEE,
      },
      {
        key: "employee-create",
        path: "/hr/employee/create",
        title: "Create Employee",
        icon: <UserOutlined />,
        component: EmployeeFormPage,
        hidden: true,
        protected: true,

      },
      {
        key: "employee-edit",
        path: "/hr/employee/edit/:id",
        title: "Edit Employee",
        icon: <UserOutlined />,
        component: EmployeeFormPage,
        hidden: true,
        protected: true,

      },
      // 2.2 Organization Setup
      {
        key: "department-management",
        path: "/hr/departments",
        title: "Department Management",
        icon: <BuildOutlined />,
        component: DepartmentManagement,
        protected: true,

        module: MODULES.DEPARTMENT,
      },
      {
        key: "department-create",
        path: "/hr/department/create",
        title: "Create Department",
        icon: <BuildOutlined />,
        component: DepartmentManagement,
        hidden: true,
        protected: true,

      },
      {
        key: "department-edit",
        path: "/hr/department/edit/:id",
        title: "Edit Department",
        icon: <BuildOutlined />,
        component: DepartmentManagement,
        hidden: true,
        protected: true,

      },
      {
        key: "designation-management",
        path: "/hr/designations",
        title: "Designation Management",
        icon: <BuildOutlined />,
        component: DesignationManagement,
        protected: true,

        module: MODULES.DESIGNATION,
      },
      {
        key: "designation-create",
        path: "/hr/designation/create",
        title: "Create Designation",
        icon: <BuildOutlined />,
        component: DesignationManagement,
        hidden: true,
        protected: true,
      },
      {
        key: "designation-edit",
        path: "/hr/designation/edit/:id",
        title: "Edit Designation",
        icon: <BuildOutlined />,
        component: DesignationManagement,
        hidden: true,
        protected: true,
      },
      {
        key: "worker-type-management",
        path: "/hr/worker-types",
        title: "Worker Type Management",
        icon: <BuildOutlined />,
        component: WorkerTypeManagement,
        protected: true,

},
      {
        key: "worker-type-create",
        path: "/hr/worker-type/create",
        title: "Create Worker Type",
        icon: <BuildOutlined />,
        component: WorkerTypeManagement,
        hidden: true,
        protected: true,

      },
      {
        key: "worker-type-edit",
        path: "/hr/worker-type/edit/:id",
        title: "Edit Worker Type",
        icon: <BuildOutlined />,
        component: WorkerTypeManagement,
        hidden: true,
        protected: true,

      },
      // 2.3 Leave Management
      {
        key: "leave-master",
        path: "/hr/leave-master",
        title: "Leave Master",
        icon: <CalendarOutlined />,
        component: LeaveMaster,
        protected: true,

        module: MODULES.LEAVE,
      },
      {
        key: "holiday-master",
        path: "/hr/holiday-master",
        title: "Holiday Master",
        icon: <CalendarOutlined />,
        component: HolidayMaster,
        protected: true,

        module: MODULES.LEAVE,
      },
      {
        key: "leave-allocation",
        path: "/hr/employee-leave-allocation",
        title: "Leave Allocation",
        icon: <CalendarOutlined />,
        component: EmployeeLeaveAllocationMaster,
        protected: true,

        module: MODULES.LEAVE,
      },
      {
        key: "apply-leave",
        path: "/hr/employee-apply-leave",
        title: "Leave Approvals",
        icon: <CalendarOutlined />,
        component: EmployeeApplyLeave,
        protected: true,

        module: MODULES.LEAVE,
      },
      {
        key: "leave-encashments",
        path: "/hr/leave-encashments",
        title: "Leave Encashment",
        icon: <DollarOutlined />,
        component: LeaveEncashment,
        protected: true,

        module: MODULES.LEAVE,
      },
      {
        key: "leave-carry-forwards",
        path: "/hr/leave-carry-forwards",
        title: "Leave Carry Forward",
        icon: <CalendarOutlined />,
        component: LeaveCarryForward,
        protected: true,

        module: MODULES.LEAVE,
      },
      // 2.4 Time Management
      {
        key: "shift-master",
        path: "/hr/shift-master",
        title: "Shift Master",
        icon: <ClockCircleOutlined />,
        component: ShiftMaster,
        protected: true,

        module: MODULES.SHIFT,
      },
      {
        key: "attendance-records",
        path: "/hr/attendance-records",
        title: "Attendance Approvals & Records",
        icon: <ClockCircleOutlined />,
        component: AttendanceRecordMaster,
        protected: true,

        module: MODULES.ATTENDANCE,
      },
      // 2.5 Reimbursement Management
      {
        key: "reimbursement-types",
        path: "/hr/reimbursement-types",
        title: "Reimbursement Types",
        icon: <BankOutlined />,
        component: ReimbursementTypeMaster,
        protected: true,

        hidden: true,
        module: MODULES.REIMBURSEMENT,
      },
      {
        key: "employee-reimbursements",
        path: "/hr/employee-reimbursements",
        title: "Employee Reimbursements",
        icon: <BankOutlined />,
        component: EmployeeReimbursementMaster,
        protected: true,
        hidden: true,
        module: MODULES.REIMBURSEMENT,
      },
      // 2.6 Performance & Development
      {
        key: "performance-reviews",
        path: "/hr/performance-reviews",
        title: "Performance Reviews",
        icon: <CrownOutlined />,
        component: PerformanceReview,
        protected: true,
        hidden: true,
        module: MODULES.PERFORMANCE,
      },
      {
        key: "performance-goals",
        path: "/hr/performance-goals",
        title: "Performance Goals",
        icon: <AimOutlined />,
        component: PerformanceGoal,
        protected: true,
        hidden: true,
        module: MODULES.PERFORMANCE,
      },
      {
        key: "training-programs",
        path: "/hr/training-programs",
        title: "Training Programs",
        icon: <BookOutlined />,
        component: TrainingProgram,
        protected: true,
        hidden: true,
        module: MODULES.TRAINING,
      },
      {
        key: "employee-trainings",
        path: "/hr/employee-trainings",
        title: "Employee Trainings",
        icon: <SafetyCertificateOutlined />,
        component: EmployeeTraining,
        protected: true,
        hidden: true,
        module: MODULES.TRAINING,
      },
      // 2.7 Recruitment
      {
        key: "job-postings",
        path: "/hr/job-postings",
        title: "Job Postings",
        icon: <FileTextOutlined />,
        component: JobPosting,
        protected: true,
        hidden: true,
        module: MODULES.RECRUITMENT,
      },
      {
        key: "job-applications",
        path: "/hr/job-applications",
        title: "Job Applications",
        icon: <FileTextOutlined />,
        component: JobApplication,
        protected: true,
        hidden: true,
        module: MODULES.RECRUITMENT,
      },
      {
        key: "interview-feedback",
        path: "/hr/interview-feedback",
        title: "Interview Feedback",
        icon: <FileTextOutlined />,
        component: InterviewFeedback,
        protected: true,
        hidden: true,
        module: MODULES.RECRUITMENT,
      },
      // 2.8 Notifications
      {
        key: "notifications",
        path: "/hr/notifications",
        title: "Notifications",
        icon: <BellOutlined />,
        component: NotificationCenter,
        protected: true,
      },
    ],
  },
  // 3. Payroll Management
  {
    key: "payroll",
    path: "/payroll",
    title: "Payroll Management",
    icon: <DollarOutlined />,
    protected: true,

    module: MODULES.PAYROLL,
    children: [
      // 3.1 Payroll Setup
      {
        key: "payroll-components",
        path: "/payroll/payroll-components",
        title: "Payroll Components",
        icon: <CalculatorOutlined />,
        component: PayrollComponentMaster,
        protected: true,
      },
      {
        key: "payslip-components",
        path: "/payroll/payslip-components",
        title: "Payslip Components",
        icon: <FileProtectOutlined />,
        component: PayslipComponentMaster,
        protected: true,
      },
      {
        key: "employee-salary-structures",
        path: "/payroll/employee-salary-structures",
        title: "Salary Structures",
        icon: <DollarOutlined />,
        component: EmployeeSalaryStructureMaster,
        protected: true,
      },
      {
        key: "salary-templates",
        path: "/payroll/salary-templates",
        title: "Salary Templates",
        icon: <FileProtectOutlined />,
        component: SalaryTemplateMaster,
        protected: true,
      },
      {
        key: "employee-template-assignments",
        path: "/payroll/employee-template-assignments",
        title: "Template Assignments",
        icon: <UserOutlined />,
        component: EmployeeTemplateAssignment,
        protected: true,
      },
      // 3.2 Payslip Management
      {
        key: "payslips",
        path: "/payroll/payslips",
        title: "Payslips",
        icon: <FileTextOutlined />,
        component: PayslipMaster,
        protected: true,
      },
      {
        key: "payslip-create",
        path: "/payroll/payslip/create",
        title: "Create Payslip",
        icon: <FileTextOutlined />,
        component: PayslipMaster,
        hidden: true,
        protected: true,
      },
      {
        key: "payslip-edit",
        path: "/payroll/payslip/edit/:id",
        title: "Edit Payslip",
        icon: <FileTextOutlined />,
        component: PayslipMaster,
        hidden: true,
        protected: true,
      },
      {
        key: "payslip-view",
        path: "/payroll/payslip/view/:id",
        title: "View Payslip",
        icon: <FileTextOutlined />,
        component: PayslipView,
        hidden: true,
        protected: true,
      },
      // 3.3 Payslip Generation
      {
        key: "payslip-generator",
        path: "/payroll/payslip-generator",
        title: "Payslip Generator",
        icon: <CalculatorOutlined />,
        component: PayslipGenerator,
        protected: true,
      },
      {
        key: "bulk-payslip-generator",
        path: "/payroll/bulk-payslip-generator",
        title: "Bulk Payslip Generator",
        icon: <FileTextOutlined />,
        component: BulkPayslipGenerator,
        protected: true,
      },
      {
        key: "payslip-status-manager",
        path: "/payroll/payslip-status-manager",
        title: "Payslip Status Manager",
        icon: <FileTextOutlined />,
        component: PayslipStatusManager,
        protected: true,
      },
      // 3.4 Payroll Analytics
      {
        key: "payroll-analytics",
        path: "/payroll/analytics",
        title: "Payroll Analytics",
        icon: <BarChartOutlined />,
        component: PayrollAnalytics,
        protected: true,
      },
      // 3.5 Payroll Tools (Bank export, Form 16, LOP sync)
      {
        key: "payroll-tools",
        path: "/payroll/tools",
        title: "Payroll Tools",
        icon: <BankOutlined />,
        component: PayrollTools,
        protected: true,
      },
    ],
  },
  // 4. Timesheet Management
  {
    key: "timesheet",
    path: "/timesheet",
    title: "Timesheet Management",
    icon: <FieldTimeOutlined />,
    protected: true,

    module: MODULES.TIMESHEET,
    children: [
      {
        key: "timesheet-master",
        path: "/timesheet/timesheet-master",
        title: "Timesheet Management",
        icon: <ClockCircleOutlined />,
        component: TimesheetMaster,
        hidden: true,
        protected: true,
        module: MODULES.TIMESHEET,
      },
      {
        key: "timesheet-create",
        path: "/timesheet/create",
        title: "Create Timesheet",
        icon: <ClockCircleOutlined />,
        component: TimeEntryMaster,
        hidden: true,
        protected: true,
      },
      {
        key: "timesheet-edit",
        path: "/timesheet/edit/:id",
        title: "Edit Timesheet",
        icon: <ClockCircleOutlined />,
        component: TimeEntryMaster,
        hidden: true,
        protected: true,
      },
      {
        key: "weekly-timesheet-master",
        path: "/timesheet/weekly-timesheet-master",
        title: "Timesheet Approvals",
        icon: <ClockCircleOutlined />,
        component: WeeklyTimesheetMaster,
        protected: true,
      },
      {
        key: "weekly-timesheet-create",
        path: "/timesheet/weekly-timesheet/create",
        title: "Create Weekly Timesheet",
        icon: <ClockCircleOutlined />,
        component: WeeklyTimesheetForm,
        hidden: true,
        protected: true,
      },
      {
        key: "weekly-timesheet-edit",
        path: "/timesheet/weekly-timesheet/edit/:id",
        title: "Edit Weekly Timesheet",
        icon: <ClockCircleOutlined />,
        component: WeeklyTimesheetForm,
        hidden: true,
        protected: true,
      },
    ],
  },
  // 5. Project Management
  {
    key: "projects",
    path: "/projects",
    title: "Project Management",
    icon: <ProjectOutlined />,
    protected: true,

    module: MODULES.PROJECT,
    children: [
      {
        key: "project-master",
        path: "/projects/project-master",
        title: "Project Master",
        icon: <FileTextOutlined />,
        component: ProjectMaster,
        protected: true,
        module: MODULES.PROJECT,
      },
      {
        key: "project-assignments",
        path: "/projects/project-assignments",
        title: "Project Assignments",
        icon: <UserOutlined />,
        component: ProjectAssignmentMaster,
        protected: true,
        module: MODULES.PROJECT,
      },
    ],
  },
  // 6. CRM & Sales
  {
    key: "crm",
    path: "/crm",
    title: "CRM & Sales",
    icon: <ShopOutlined />,
    protected: true,

    module: MODULES.CLIENT,
    children: [
      {
        key: "company-master",
        path: "/crm/customer-master",
        title: "Companies",
        icon: <UserOutlined />,
        component: CompanyMaster,
        protected: true,
        module: MODULES.CLIENT,
      },
      {
        key: "company-contacts",
        path: "/crm/company-contacts",
        title: "Company Contacts",
        icon: <UserOutlined />,
        component: CompanyContactMaster,
        protected: true,
        module: MODULES.CLIENT,
      },
      {
        key: "clients",
        path: "/crm/clients",
        title: "Clients",
        icon: <UserOutlined />,
        component: ClientMaster,
        protected: true,
        module: MODULES.CLIENT,
      },
      {
        key: "lead-sources",
        path: "/crm/lead-sources",
        title: "Lead Sources",
        icon: <AimOutlined />,
        component: LeadSourceMaster,
        protected: true,
        module: MODULES.CLIENT,
      },
      {
        key: "leads",
        path: "/crm/leads",
        title: "Leads",
        icon: <UserOutlined />,
        component: LeadMaster,
        protected: true,
        module: MODULES.CLIENT,
      },
      {
        key: "opportunities",
        path: "/crm/opportunities",
        title: "Opportunities",
        icon: <DollarOutlined />,
        component: OpportunityMaster,
        protected: true,
        module: MODULES.CLIENT,
      },
      {
        key: "products",
        path: "/crm/products",
        title: "Products & Services",
        icon: <AppstoreOutlined />,
        component: ProductMaster,
        protected: true,
        module: MODULES.CLIENT,
      },
      {
        key: "quotations",
        path: "/crm/quotations",
        title: "Quotations",
        icon: <FileTextOutlined />,
        component: Quotations,
        protected: true,
        module: MODULES.QUOTATION,
      },
    ],
  },
  // 7. System Administration (Last - Admin Only)
  {
    key: "admin",
    path: "/admin",
    title: "System Administration",
    icon: <SettingOutlined />,
    protected: true,

    module: MODULES.ADMIN,
    children: [
      // 7.1 Role & Permission Management
      {
        key: "role-permission-dashboard",
        path: "/admin/role-permission-dashboard",
        title: "Role & Permission Dashboard",
        icon: <SafetyCertificateOutlined />,
        component: RolePermissionDashboard,
        protected: true,

        module: MODULES.ADMIN,
      },
      {
        key: "role-permission-guide",
        path: "/admin/role-permission-guide",
        title: "Role & Permission Guide",
        icon: <BookOutlined />,
        component: RolePermissionGuide,
        protected: true,

      },
      // 7.2 Subscription & Plan Management
      {
        key: "tenant-management",
        path: "/admin/tenant-management",
        title: "Tenant Management",
        icon: <TeamOutlined />,
        component: TenantManagement,
        protected: true,
        requiredRoles: [ROLES.SUPER_ADMIN],
        module: MODULES.ADMIN,
      },
      {
        key: "subscription-management",
        path: "/admin/subscription-management",
        title: "Subscription Management",
        icon: <DollarOutlined />,
        component: SubscriptionManagement,
        protected: true,
        requiredRoles: [ROLES.SUPER_ADMIN],
        module: MODULES.ADMIN,
      },
      {
        key: "plan-route-management",
        path: "/admin/plan-route-management",
        title: "Plan Route Management",
        icon: <BranchesOutlined />,
        component: lazy(() => import("../Pages/Admin/PlanRouteManagement")),
        protected: true,
        requiredRoles: [ROLES.SUPER_ADMIN],
        module: MODULES.ADMIN,
      },
      // 7.3 System Configuration
      {
        key: "admin-policies",
        path: "/admin/company-policies",
        title: "Company Policies",
        icon: <FileProtectOutlined />,
        component: AdminPoliciesPage,
        protected: true,
        module: MODULES.ADMIN,
      },
      {
        key: "admin-employee-documents",
        path: "/admin/employee-documents",
        title: "Offer & Experience Letters",
        icon: <FileProtectOutlined />,
        component: AdminEmployeeDocumentsPage,
        protected: true,
        module: MODULES.ADMIN,
      },
      {
        key: "system-config",
        path: "/admin/system-config",
        title: "System Configuration",
        icon: <SettingOutlined />,
        component: SystemConfiguration,
        protected: true,
        requiredRoles: [ROLES.SUPER_ADMIN],
        module: MODULES.SYSTEM,
      },
      // 7.4 Tenant Configuration (theme, branding for payslip, quotation, PO, sales order)
      {
        key: "tenant-config",
        path: "/admin/tenant-config",
        title: "Tenant Configuration",
        icon: <SettingOutlined />,
        component: TenantConfig,
        protected: true,

        module: MODULES.ADMIN,
      },
    ],
  },
  // Hidden Design & Development (for future use)
  {
    key: "design",
    path: "/design",
    title: "Design & Development",
    icon: <BuildOutlined />,
    protected: true,
    hidden: true,
    children: [
      {
        key: "item-master",
        path: "/design/item-master",
        title: "Item Master",
        icon: <UserOutlined />,
        component: ItemMaster,
        protected: true,
      },
    ],
  },
];

export default routes;
